import { supabase } from '@/lib/supabase'
import { metricsApi } from '@/api/metrics'
import type {
  CronHealthRow,
  MetricsSeriesRow,
  OrgCostEstimateRow,
  OrgQuotaUsageRow,
  ServiceAlertRow,
} from '@/types'

export type MonitorSourceResult<T> = {
  data: T
  error: string | null
  fetchedAt: string
}

export type WatchdogStatus = {
  ok: boolean
  consecutiveFailures: number
  lastNotifiedAt: string | null
  lastHealthySummaryAt: string | null
  lastCheckedAt: string | null
  lastHealthyAt: string | null
  lastReason: string | null
  evaluatorLastRunAt: string | null
  evaluatorAgeSecs: number | null
  notificationConfigured: boolean
  pollIntervalMinutes: number
  failuresBeforeNotify: number
  fetchedAt: string
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message: unknown }).message)
  }
  return 'unknown error'
}

async function sourceResult<T>(load: () => Promise<T>, fallback: T): Promise<MonitorSourceResult<T>> {
  const fetchedAt = new Date().toISOString()
  try {
    return { data: await load(), error: null, fetchedAt }
  } catch (err) {
    const error = errorMessage(err)
    console.warn('[servicesMonitor]', error)
    return { data: fallback, error, fetchedAt }
  }
}

export const servicesMonitorApi = {
  watchdogStatus: (): Promise<MonitorSourceResult<WatchdogStatus | null>> =>
    sourceResult(async () => {
      const { data, error } = await supabase.functions.invoke('admin-watchdog-status')
      if (error) throw error
      if (!data?.ok) throw new Error(data?.error ?? 'watchdog status unavailable')
      return data as WatchdogStatus
    }, null),

  quotaUsage: (): Promise<MonitorSourceResult<OrgQuotaUsageRow[]>> =>
    sourceResult(async () => {
      const { data, error } = await supabase.rpc('admin_org_quota_usage')
      if (error) throw error
      return (data ?? []) as OrgQuotaUsageRow[]
    }, []),

  costEstimate: (): Promise<MonitorSourceResult<OrgCostEstimateRow[]>> =>
    sourceResult(async () => {
      const { data, error } = await supabase.rpc('admin_org_cost_estimate')
      if (error) throw error
      return (data ?? []) as OrgCostEstimateRow[]
    }, []),

  alerts: (includeResolved = true): Promise<MonitorSourceResult<ServiceAlertRow[]>> =>
    sourceResult(async () => {
      const { data, error } = await supabase.rpc('admin_service_alerts', {
        p_include_resolved: includeResolved,
      })
      if (error) throw error
      return (data ?? []) as ServiceAlertRow[]
    }, []),

  cronHealth: (): Promise<MonitorSourceResult<CronHealthRow[]>> =>
    sourceResult(async () => {
      const { data, error } = await supabase.rpc('admin_cron_health')
      if (error) throw error
      return (data ?? []) as CronHealthRow[]
    }, []),

  dbSeries: (since?: string): Promise<MonitorSourceResult<MetricsSeriesRow[]>> =>
    sourceResult(async () => {
      return await metricsApi.series({
        component: 'db',
        since: since ?? new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      })
    }, []),

  waSeries: (since?: string): Promise<MonitorSourceResult<MetricsSeriesRow[]>> =>
    sourceResult(async () => {
      const pSince = since ?? new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
      const [waHost, waCanary] = await Promise.all([
        metricsApi.series({ component: 'wa-prod-1', since: pSince }),
        metricsApi.series({ component: 'wa', metric: 'canary_ok', since: pSince }),
      ])
      return [...waHost, ...waCanary]
    }, []),
}
